/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import {
  TableBody,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { useRecoilState, useSetRecoilState } from 'recoil';
import LoadingOverlay from 'react-loading-overlay';
import AuthenticatedContext from '../../context/AuthenticatedContext';
import {
  backPageFlg,
  deviceListPageInfo,
  pageTitle,
} from '../../context/PageRecoil';
import Device from '../../models/data/Device';
import { pathnames } from '../../api';
import requestDeviceList, { requestCountDevice } from '../../api/device';
import useStyles, { headerWidth } from '../../css/style';
import Paging from '../paging';
import { OneEquipment } from '../../models/data/Equipment';
import { requestOneEquipment } from '../../api/equipment';
import DataDisp from './datadisp';
import { routeName } from '../../App';
import useErrorHandling from '../errorCheck';

type UserPageParams = {
  id: string;
};

/**
 * 設備機器一覧画面
 */
const Devicelist: React.FunctionComponent = () => {
  const history = useHistory();
  const [pageInfo, setPageInfo] = useRecoilState(deviceListPageInfo);
  const authContext = useContext(AuthenticatedContext); // ログインユーザの情報を取得
  const classes = useStyles();
  const [loading, setLoading] = useState(true); // ロード画面の状態管理
  const { id } = useParams<UserPageParams>();
  const [backFlg, setBackFlg] = useRecoilState(backPageFlg);
  const [data, setData] = useState<Device[]>([]); // 表示するメインデータ
  const [subdata, setSubData] = useState<OneEquipment>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState<number>(0); // テーブルのスクロール位置
  const { errorCheck } = useErrorHandling(); // エラー処理

  const setPageTitle = useSetRecoilState(pageTitle);
  React.useEffect(() => {
    return setPageTitle({ pageTitle: '設備機器一覧' }); // ページのタイトルを設定
  }, [setPageTitle]);

  // APIのURL
  const urlDeviceList = history.createHref({ pathname: pathnames.deviceList }); // 機器一覧取得
  const urlCountDevice = history.createHref({
    pathname: pathnames.deviceCount,
  }); // 機器件数取得
  const urlOneEquipment = history.createHref({
    pathname: pathnames.oneEquipment,
  });
  // ページング用の変数
  const [totalData, setTotalData] = useState<number>(0); // データ数
  const [currentPage, setCurrentPage] = useState<number>(1); // 現在のページ
  const perPage = 20; // 一覧一ページあたりの表示件数

  // ソート用の変数
  type SortConfigType = { key: string; direction: string };
  const [sortConfig, setSortConfig] = React.useState<SortConfigType>({
    key: '',
    direction: '',
  });

  const headerCellProps = [
    {
      width: headerWidth.mstId,
      sortKey: 'mstId',
      text: '機器ID',
      className: classes.header,
    },
    {
      width: headerWidth.dvButton1,
      className: classes.header,
      // ボタン項目にsortKeyやtextの設定はなし
    },
    {
      width: headerWidth.dvButton2,
      className: classes.header,
      // ボタン項目にsortKeyやtextの設定はなし
    },
    {
      width: headerWidth.roomName,
      sortKey: 'roomName',
      text: '機器設置場所',
      className: classes.header,
    },
    {
      width: headerWidth.deviceName,
      sortKey: 'deviceName',
      text: '機器名',
      className: classes.header,
    },
    {
      width: headerWidth.tagNo,
      sortKey: 'tagNo',
      text: '機器番号',
      className: classes.header,
    },
    {
      width: headerWidth.eqType,
      sortKey: 'eqType',
      text: '形式',
      className: classes.header,
    },
    {
      width: headerWidth.seizoNo,
      sortKey: 'seizoNo',
      text: '製造番号',
      className: classes.header,
    },
    {
      width: headerWidth.makerName,
      sortKey: 'makerName',
      text: 'メーカ名',
      className: classes.header,
    },
  ];

  // 画面遷移で送られた情報を設定する
  const locationSet = () => {
    // 初期値のままの場合
    if (currentPage === 0) {
      setCurrentPage(1);
    }
    // 戻るでの状態再現のフラグがTrueの場合
    if (backFlg.deviceList) {
      setSortConfig({
        key: pageInfo.sortKey,
        direction: pageInfo.sortDirection,
      });
      setCurrentPage(pageInfo.page);
      // 一覧のテーブルがあるか確認
      if (containerRef.current) {
        setScroll(pageInfo.scrollPosition);
      }
      setBackFlg({ ...backFlg, deviceList: false });
    }
  };

  // 一覧のページ変更処理
  const pageChange = (num: number) => {
    setScroll(0);
    setCurrentPage((prevPage) => {
      let newPage = prevPage + num;
      const tempTotalPages = Math.ceil(totalData / perPage);
      const totalPages = tempTotalPages > 0 ? tempTotalPages : 1;

      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;

      return newPage;
    });
  };

  // ソート機能の関数など
  const sortStart = (key: string) => {
    pageChange(1 - currentPage); // はじめのページに戻る
    setScroll(0); // 表のスクロールバーをリセット
    let direction = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // ページ遷移時の処理
  const movePage = (route: string) => {
    const tempScroll = containerRef.current
      ? containerRef.current.scrollTop
      : 0;
    setPageInfo({
      page: currentPage,
      scrollPosition: tempScroll,
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction,
    });
    history.push(route);
  };

  // 初回画面表示時の処理
  useEffect(() => {
    setLoading(true); // 非同期処理前にsetLoading(true)
    locationSet();
    Promise.all([
      requestOneEquipment(urlOneEquipment, {
        equipmentId: Number(id),
      }),
    ])
      .then((responses) => {
        setSubData(responses[0].data); // 設備情報
      })
      .catch((error) => {
        errorCheck(error); // エラー内容を確認
      })
      .finally(() => {
        setLoading(false); // 非同期処理後にsetLoading(false)
      });
  }, []);

  // データ更新時の処理
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scroll;
    }
  }, [data]);

  // 機器情報の取得
  useEffect(() => {
    setLoading(true); // 非同期処理前にsetLoading(true)
    Promise.all([
      requestCountDevice(urlCountDevice, {
        custEqId: Number(id),
      }),
      requestDeviceList(urlDeviceList, {
        custEqId: Number(id),
        number: perPage,
        page: currentPage,
        sortKey: sortConfig.key,
        sortDirection: sortConfig.direction,
      }),
    ])
      .then((responses) => {
        setTotalData(responses[0].data); // 件数
        setData(responses[1].data); // 設備情報
      })
      .catch((error) => {
        errorCheck(error); // エラー内容を確認
      })
      .finally(() => {
        setLoading(false); // 非同期処理後にsetLoading(false)
      });
  }, [currentPage, sortConfig]);

  return (
    <LoadingOverlay
      active={loading}
      className={classes.load}
      spinner
      text="データを読み込んでいます..."
    >
      <div className={classes.root}>
        <Button
          variant="primary"
          className="my-2"
          onClick={() => {
            setBackFlg({ ...backFlg, eqList: true });
            history.push(routeName.eqList);
          }}
        >
          戻る
        </Button>
        <br />
        <DataDisp userdata={authContext} data={subdata} />
        <div className={`${classes.paging} ${classes.deviceListWidth}`}>
          <Paging
            totalData={totalData}
            currentPage={currentPage}
            perPage={perPage}
            onPageChange={pageChange}
          />
        </div>
        <TableContainer
          component={Paper}
          className={`${classes.container} ${classes.deviceListHeight} ${classes.deviceListWidth}`}
          ref={containerRef}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* ヘッダのセルを生成 */}
                {headerCellProps.map(({ width, sortKey, text, className }) => (
                  <TableCell width={width} className={className}>
                    {sortKey != null && (
                      <Button
                        variant="link"
                        onClick={() => sortStart(sortKey)}
                        className={classes.linkButton}
                      >
                        {text}
                        {sortConfig &&
                          sortConfig.key === sortKey &&
                          (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((d, index) => (
                <TableRow
                  key={d.mstId}
                  className={index % 2 === 0 ? classes.evenRow : classes.oddRow}
                >
                  <TableCell className={`${classes.cell} ${classes.numRow}`}>
                    <Button
                      variant="link"
                      onClick={() =>
                        movePage(`${routeName.deviceDetail}/${id}/${d.mstId}`)
                      }
                    >
                      {d.mstId}
                    </Button>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.buttonRow}`}>
                    <Button
                      variant="outline-primary"
                      className="mb-1"
                      onClick={() =>
                        movePage(`${routeName.partsList}/${id}/${d.mstId}`)
                      }
                    >
                      部品一覧
                    </Button>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.buttonRow}`}>
                    <Button
                      variant="outline-primary"
                      onClick={() =>
                        movePage(
                          `${routeName.deviceMaintMenu}/${id}/${d.mstId}`,
                        )
                      }
                    >
                      点検MENU
                    </Button>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.roomName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.deviceName}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.tagNo}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.eqType}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.seizoNo}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.stringRow}`}>
                    {d.makerName}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </LoadingOverlay>
  );
};

export default Devicelist;
